import {tokens, colors} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

import {ProgressBarWithText} from './progress-bar-with-text'
import type {Location, LocationNames} from '../shared'
import {currencyFormatter, getReplacePlaceholders} from '../shared'

interface PackageItem {
    title: string
    value: number
}

interface Props {
    title: string
    budgetTotal: number
    budgetUsed: number
    budgetAllowanceText: string
    packageTitle: string
    progressBarText: string
    location: Location
    packageItems: PackageItem[]
    totalBenefitsTitle: string
    note: string
    className?: string
    locationNames: LocationNames
}

export const Results = ({
    title,
    budgetTotal,
    budgetUsed,
    budgetAllowanceText,
    packageTitle,
    location,
    packageItems,
    progressBarText,
    totalBenefitsTitle,
    note,
    className,
    locationNames,
}: Props) => {
    const replacePlaceholders = getReplacePlaceholders({
        location,
        budgetTotal,
        budgetUsed,
        locationNames,
    })
    const formatCurrency = currencyFormatter(location)
    const totalAccruedBenefits = packageItems.reduce(
        (acc, currentValue) => acc + currentValue.value,
        0
    )

    return (
        <Container className={className}>
            <ProgressContainer>
                <ProgressTitle forwardedAs="h3" variant="spezia">
                    {title}
                </ProgressTitle>

                <AllowedBudgetContent variant="paragraph-large">
                    {replacePlaceholders(budgetAllowanceText)}
                </AllowedBudgetContent>

                <ProgressBarWithText
                    budgetTotal={budgetTotal}
                    budgetUsed={budgetUsed}
                    location={location}
                    progressBarText={progressBarText}
                    locationNames={locationNames}
                />
            </ProgressContainer>

            <PackageDetailsContainer>
                <PackageDetailsTitle forwardedAs="h3" variant="spezia">
                    {packageTitle}
                </PackageDetailsTitle>

                {packageItems.map((packageItem) => (
                    <StyledPackageItem key={packageItem.title}>
                        <Text variant="paragraph-large">{packageItem.title}</Text>
                        <Text variant="paragraph-large">{formatCurrency(packageItem.value)}</Text>
                    </StyledPackageItem>
                ))}

                <StyledPackageItem>
                    <Text variant="paragraph-large">
                        <strong>{totalBenefitsTitle}</strong>
                    </Text>

                    <Text as="output" variant="paragraph-large">
                        <strong>{formatCurrency(totalAccruedBenefits)}</strong>
                    </Text>
                </StyledPackageItem>
            </PackageDetailsContainer>

            <Note variant="label-base">{note}</Note>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
`

const PackageDetailsTitle = styled(Text)`
    margin-bottom: ${tokens.spacing32};
`

const PackageDetailsContainer = styled.div`
    padding: ${tokens.spacing40} ${tokens.spacing24};
    background: ${colors.background.pink};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
        border-radius: 0 0 ${tokens.arc8} ${tokens.arc8};
    }
`

const StyledPackageItem = styled.div`
    display: flex;
    justify-content: space-between;

    &:last-of-type {
        margin-top: ${tokens.spacing32};
    }
`

const ProgressContainer = styled.div`
    padding: ${tokens.spacing40} ${tokens.spacing24};
    background: ${colors.background.muted};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
        border-radius: ${tokens.arc8} ${tokens.arc8} 0 0;
    }
`
const ProgressTitle = styled(Text)`
    margin-bottom: ${tokens.spacing10};
`
const AllowedBudgetContent = styled(Text)`
    margin-bottom: ${tokens.spacing12};
`

const Note = styled(Text)`
    padding: ${tokens.spacing20} ${tokens.spacing24};

    ${mq.min('sm')} {
        padding: ${tokens.spacing20} 0 0;
    }
`
